import { useState, useRef, useCallback } from "react"
import { useEventEffect } from "./useEventEffect"
import type { Coordinates, EventListener } from "./types"
import type { DependencyList } from "react"

interface HookProps {
  target: Window | Element
}

interface State extends Coordinates {
  direction: Coordinates<1 | 0 | -1>
}

const initialState: State = {
  direction: {
    x: 0,
    y: 0,
  },
  x: 0,
  y: 0,
}

export const useScrollPosition = ({ target }: HookProps) => {
  const [position, setPosition] = useState(initialState)
  const value = useRef<Coordinates>({ x: 0, y: 0 })
  const callback: EventListener  = useCallback(() => {
    const { scrollTop, scrollLeft } = target instanceof Window 
      ? document.documentElement : target
    const newCoordinates = { x: scrollLeft, y: scrollTop }
    setPosition({
      ...newCoordinates,
      direction: {
        x: scrollLeft > value.current.x ? 1 : -1,
        y: scrollTop > value.current.y ? 1 : -1,
      }
    })
    value.current = newCoordinates
  }, [setPosition, target])

  useEventEffect({ callback, types: ['scroll'], options: { passive: true }})
  return position
}

/* eslint-disable react-hooks/rules-of-hooks */
useScrollPosition.Effect = (
  listener: (state: State) => void,
  deps: DependencyList = [],
  target?: HookProps['target'],
) => {
  const value = useRef<Coordinates>({ x: 0, y: 0 })
  const callback = useCallback(() => {
    const scrollTarget = target ?? window
    const { scrollTop, scrollLeft } = scrollTarget instanceof Window 
      ? document.documentElement : scrollTarget
    const newCoordinates: Coordinates = { x: scrollLeft, y: scrollTop }
    listener({ ...newCoordinates, direction: {
      x: scrollLeft > value.current.x ? 1 : -1,
      y: scrollTop > value.current.y ? 1 : -1,
    }})
    value.current = newCoordinates
  }, [listener, target, ...deps])
  
  useEventEffect({ callback, types: ['scroll'], options: { passive: true }})
}
